import React, { useEffect, useMemo, useState } from 'react'
import auth from '../../auth/service'
import Callback from './Callback'

export const CALLBACK_FEEDBACK_TIMEOUT_MS = 10000

const DEFAULT_AUTH_ERROR = {
  error: 'authentication_failed',
  errorDescription: 'Non siamo riusciti a completare il login.',
}

const INVALID_CALLBACK_ERROR = {
  error: 'invalid_callback',
  errorDescription: 'Il link di accesso non e valido o risulta scaduto.',
}

const hasAuthResponse = (hash = '') => /access_token|id_token|error/.test(hash)

const getReferrer = (search = '') => {
  const referrer = new URLSearchParams(search).get('referrer')
  return referrer || '/'
}

export const getAuthenticationErrorMessage = (error) => {
  const code = String((error && (error.error || error.code)) || '').toLowerCase()
  const description = String(
    (error && (error.errorDescription || error.description || error.message)) || ''
  ).toLowerCase()
  const normalized = `${code} ${description}`.trim()

  if (
    normalized.includes('login_required') ||
    normalized.includes('expired') ||
    normalized.includes('session')
  ) {
    return "La sessione e' scaduta o non e' piu' valida. Effettua di nuovo il login."
  }

  if (normalized.includes('access_denied') || normalized.includes('unauthorized')) {
    return "L'accesso e' stato rifiutato. Verifica i permessi e riprova."
  }

  if (normalized.includes('invalid_callback')) {
    return "Il link di accesso non e' piu' valido. Avvia di nuovo il login."
  }

  return 'Non siamo riusciti a completare il login. Riprova tra qualche istante.'
}

export const getAuthenticationErrorDetails = (error) =>
  (error && (error.errorDescription || error.description || error.message)) || null

const buildActions = (history, referrer) => (
  <>
    <button
      type="button"
      className="btn btn-primary mb-2 mr-sm-2"
      onClick={() => auth.login(referrer)}
    >
      Riprova login
    </button>
    <button
      type="button"
      className="btn btn-outline-secondary mb-2"
      onClick={() => history.replace('/')}
    >
      Torna alla home
    </button>
  </>
)

export const AuthCallbackRoute = ({ history, location }) => {
  const hash = (location && location.hash) || ''
  const referrer = useMemo(() => getReferrer((location && location.search) || ''), [location])
  const [authState, setAuthState] = useState({
    error: null,
    status: 'loading',
  })

  useEffect(() => {
    let isMounted = true
    const feedbackTimeout = window.setTimeout(() => {
      if (!isMounted) {
        return
      }

      setAuthState((currentState) =>
        currentState.status === 'loading'
          ? {
              ...currentState,
              status: 'slow',
            }
          : currentState
      )
    }, CALLBACK_FEEDBACK_TIMEOUT_MS)

    if (!hasAuthResponse(hash)) {
      window.clearTimeout(feedbackTimeout)
      setAuthState({
        error: INVALID_CALLBACK_ERROR,
        status: 'error',
      })

      return () => {
        isMounted = false
      }
    }

    auth
      .handleAuthentication()
      .then(() => {
        window.clearTimeout(feedbackTimeout)

        if (isMounted) {
          history.replace(referrer)
        }
      })
      .catch((error) => {
        window.clearTimeout(feedbackTimeout)
        console.error('Authentication callback failed', error)

        if (isMounted) {
          setAuthState({
            error: error || DEFAULT_AUTH_ERROR,
            status: 'error',
          })
        }
      })

    return () => {
      isMounted = false
      window.clearTimeout(feedbackTimeout)
    }
  }, [hash, history, referrer])

  if (authState.status === 'error') {
    return (
      <Callback
        isLoading={false}
        title="Accesso non riuscito"
        message={getAuthenticationErrorMessage(authState.error)}
        details={getAuthenticationErrorDetails(authState.error)}
        actions={buildActions(history, referrer)}
      />
    )
  }

  if (authState.status === 'slow') {
    return (
      <Callback
        title="Accesso in corso"
        message="L'autenticazione sta richiedendo piu' tempo del previsto. Se la pagina resta bloccata, riprova il login."
        actions={buildActions(history, referrer)}
      />
    )
  }

  return (
    <Callback
      title="Accesso in corso"
      message="Stiamo completando il login. Non chiudere questa pagina."
    />
  )
}
